import { useFetchBundle, useFetchBundles } from '@/trading/tradingQueries'
import { useNavigate, useParams } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useBundleTrade from './useBundleTrade'
import calculateInitialAllocation from './bundleAllocation'
import { Button, H1, H2 } from '@/common/ui'
import BundleAbout from './bundleAbout'
import { BUNDLE_COPY } from '@/locales/en'
import { APP_ROUTES } from '@/common/constants'
import Money from '@/common/models/money'
import { SecurityOrderItem } from './types'
import { BundleBreakdown, BundleBreakdownHandle } from './bundleBreakdown'
import { BundlePastReturns } from './bundlePastReturns'
import { EthicalTagsPanel } from '@/common/ui/EthicalTagsPanel'
import { BundleGraph } from './bundleGraph'
import { BundleModal } from './bundlerModal'
import BundleSidebar from './bundleSidebar'
import { BundleCard } from './bundleCard'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'
import { analytics } from '@/common/analytics/analytics'
import { useOrderSource } from '@/common/context/useOrderSource'

export const BundlePage = () => {
    const { orderSource } = useOrderSource()
    const { bundleId } = useParams<{ bundleId: string }>()
    const [orderFormOpen, setOrderFormOpen] = useState<boolean>(false)
    const [originalAmount, setOriginalAmount] = useState<number>(0)
    const [defaultAllocations, setDefaultAllocations] = useState<Omit<SecurityOrderItem, 'requestId'>[]>([])
    const navigate = useNavigate()
    const { data: bundle, isLoading } = useFetchBundle(bundleId)
    const { data: bundles } = useFetchBundles()

    const { setOrderItems, totalInvestmentAmount, setTotalInvestmentAmount, initiateBundleTrade } = useBundleTrade()

    const headerDivRef = useRef<HTMLDivElement | null>(null)

    const additionalBundle = useMemo(() => {
        if (!bundles || bundles.length === 0) return undefined
        return bundles.find((b) => b.slug !== bundle?.slug)
    }, [bundles, bundle?.slug])

    useEffect(() => {
        const updateSticky = () => {
            const el = headerDivRef.current
            if (!el) return

            const scrollTop = window.scrollY

            if (scrollTop >= 140) {
                el.classList.add('is-sticky')
            } else {
                el.classList.remove('is-sticky')
            }
        }

        window.addEventListener('scroll', updateSticky)
        window.addEventListener('resize', updateSticky)
        // run once to set initial state
        updateSticky()

        return () => {
            window.removeEventListener('scroll', updateSticky)
            window.removeEventListener('resize', updateSticky)
        }
    }, [])

    const resetForm = () => {
        if (originalAmount === totalInvestmentAmount) return

        const { totalAmount, orderItems } = recalculateInvestmentAmount()
        setTotalInvestmentAmount(totalAmount)
        setOrderItems(orderItems)
    }

    useEffect(() => {
        if (!bundleId) {
            navigate(APP_ROUTES.DASHBOARD, { replace: true })
            return
        }

        if (isLoading) return

        if (!bundle) {
            navigate(APP_ROUTES.DASHBOARD, { replace: true })
        }
    }, [bundleId, isLoading, bundle, navigate])

    const bundleRef = useRef<BundleBreakdownHandle | null>(null)

    const instrumentsForForm = useMemo(
        () =>
            bundle?.securities.map((s) => ({
                securityCode: s.securityCode,
                securityName: s.name,
                description: s.summary,
                priceInfo: s.priceInfo,
                weight: s.weight,
            })) || [],
        [bundle],
    )

    const _initiatedBundleRef = useRef(false)
    useEffect(() => {
        if (_initiatedBundleRef.current) return
        if (!bundle?.name) return
        initiateBundleTrade(bundle.name, bundle?.slug)
        _initiatedBundleRef.current = true
    }, [bundle?.name, initiateBundleTrade, bundle?.slug])

    const recalculateInvestmentAmount = useCallback(() => {
        if (defaultAllocations.length > 0) {
            const totalAmount = new Money(defaultAllocations.reduce((sum, a) => sum + a.amount, 0)).valueWithPrecision(
                2,
            )
            return { totalAmount, orderItems: defaultAllocations }
        }

        const allocations = calculateInitialAllocation(instrumentsForForm)
        const totalAmount = new Money(allocations.reduce((sum, a) => sum + a.amount, 0)).valueWithPrecision(2)
        const orderItems = allocations.map((a) => {
            const instrument = instrumentsForForm.find((i) => i.securityCode === a.securityCode)!
            return {
                securityCode: a.securityCode,
                instrument,
                amount: a.amount,
                units: a.units,
                relevantPrice: a.relevantPrice,
                weight: totalAmount > 0 ? a.amount / totalAmount : 0,
            }
        })

        return { totalAmount, orderItems }
    }, [instrumentsForForm, defaultAllocations])

    const initializedAllocationsRef = useRef(false)
    useEffect(() => {
        if (initializedAllocationsRef.current) return
        if (!instrumentsForForm || instrumentsForForm.length === 0) return

        const { totalAmount, orderItems } = recalculateInvestmentAmount()

        setDefaultAllocations(orderItems)
        setOriginalAmount(totalAmount)
        setTotalInvestmentAmount(totalAmount)
        setOrderItems(orderItems)
        initializedAllocationsRef.current = true
    }, [instrumentsForForm, recalculateInvestmentAmount, setOrderItems, setTotalInvestmentAmount])

    useEffect(() => {
        _initiatedBundleRef.current = false
        initializedAllocationsRef.current = false
    }, [bundleId])

    const openOrderForm = (open: boolean) => {
        if (!open) {
            setOrderFormOpen(false)
            return
        }

        analytics.trackWithDefaults(ANALYTICS_EVENTS.TRADING.BUNDLE_TRADE_STARTED, {
            bundle_id: bundle?.slug,
            order_from: orderSource.orderFrom,
            order_entry_point: orderSource.orderEntryPoint,
        })
        setOrderFormOpen(true)
    }

    return (
        <div className="w-full self-stretch inline-flex flex-col justify-start items-start gap-6">
            <div className="w-full self-stretch inline-flex flex-col justify-start items-start gap-4">
                {isLoading || !bundle ? (
                    <div className="h-12 w-3/4 max-w-[560px] bg-stone-100 rounded animate-pulse" />
                ) : (
                    <H1 className="self-stretch text-4xl font-medium text-content-primary font-['Tangerine Regular'] inline-flex flex-col justify-center items-start gap-2 mb-0">
                        {bundle.name}
                    </H1>
                )}
                <div className="inline-flex flex-row justify-start items-start gap-2">
                    {isLoading || !bundle
                        ? [1, 2, 3].map((i) => <div key={i} className="h-6 w-14 bg-stone-100 rounded-2xl" />)
                        : bundle.securities.map((security) => (
                              <Button
                                  key={security.securityCode}
                                  variant={'outline'}
                                  size={'sm'}
                                  className="h-auto px-2 rounded-3xl"
                                  onClick={() => bundleRef.current?.navigateToSecurity(security.securityCode)}
                              >
                                  {security.securityCode}
                              </Button>
                          ))}
                </div>
            </div>

            <div className="w-full flex flex-col md:flex-row justify-start items-start gap-6 min-h-screen">
                <div className="w-full self-stretch inline-flex flex-col justify-start items-start md:flex-1 min-w-0 gap-12">
                    <div className="w-full self-stretch inline-flex flex-col justify-start items-start gap-6">
                        <BundleAbout bundle={bundle} />
                        <div className="w-full h-px bg-border-divider" />
                        <BundleBreakdown ref={bundleRef} securities={bundle?.securities} bundleId={bundle?.slug} />
                    </div>
                    {!isLoading && bundle && (
                        <>
                            <div className="w-full h-px bg-border-divider" />
                            <div className="w-full self-stretch inline-flex flex-col justify-start items-start gap-6">
                                <EthicalTagsPanel
                                    description={BUNDLE_COPY.ethicalTagsDescription}
                                    positive={bundle.included}
                                    avoided={bundle.avoided}
                                    onTooltipOpen={(open?: boolean) => {
                                        if (open) {
                                            analytics.trackWithDefaults(
                                                ANALYTICS_EVENTS.TRADING.BUNDLE_TOOLTIP_OPENED,
                                                {
                                                    bundle_id: bundle?.slug,
                                                    tooltip: 'ethicalTags',
                                                },
                                            )
                                        }
                                    }}
                                />
                                <div className="w-full h-px bg-border-divider" />
                            </div>
                            <BundlePastReturns bundle={bundle} />
                            <BundleGraph securityList={defaultAllocations} />
                            <div className="w-full h-px bg-border-divider" />
                            {additionalBundle && (
                                <div className="self-stretch inline-flex flex-col justify-start items-start gap-6">
                                    <H2 className="text-center justify-start text-black text-3xl  md:text-3xl font-normal font-['Serious_Sans'] leading-10 mb-0">
                                        {BUNDLE_COPY.moreBundles}
                                    </H2>
                                    <BundleCard bundle={additionalBundle} />
                                </div>
                            )}
                        </>
                    )}
                </div>

                <BundleSidebar bundle={bundle} isLoading={isLoading} onBuy={() => openOrderForm(true)} />
            </div>
            <BundleModal
                instrumentsForForm={instrumentsForForm}
                originalAmount={originalAmount}
                resetForm={resetForm}
                open={orderFormOpen}
                onOpenChange={openOrderForm}
            />
        </div>
    )
}
